import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tooltip } from "@/components/ui/tooltip";
import { ArrowRightIcon, TrashIcon } from "@phosphor-icons/react";
import { Plus } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "@/features/utils/i18n";

interface TimeSlot {
  from: string;
  to: string;
}

interface TimeSlotsProps {
  slots: TimeSlot[];
  onAddSlot: () => void;
  onRemoveSlot: (index: number) => void;
  onUpdateSlot: (index: number, field: "from" | "to", value: string) => void;
  label?: string;
  showAddButton?: boolean;
}

const TIME_OPTIONS = Array.from({ length: 96 }, (_, i) => {
  const hours = Math.floor(i / 4);
  const minutes = (i % 4) * 15;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
});

function addMinutesToTime(time: string, minutes: number): string {
  const [hours, mins] = time.split(":").map(Number);
  const totalMinutes = hours * 60 + mins + minutes;
  const newHours = Math.floor(totalMinutes / 60) % 24;
  const newMins = totalMinutes % 60;
  return `${String(newHours).padStart(2, "0")}:${String(newMins).padStart(2, "0")}`;
}

export function TimeSlots({
  slots,
  onAddSlot,
  onRemoveSlot,
  onUpdateSlot,
  label,
  showAddButton = true,
}: TimeSlotsProps) {
  const { t } = useTranslation("schedules");
  const [openFromIndex, setOpenFromIndex] = useState<number | null>(null);
  const [openToIndex, setOpenToIndex] = useState<number | null>(null);

  return (
    <div className="flex items-center gap-2 group/slots">
      {label && (
        <Tooltip content={label}>
          <div className="w-24 text-sm font-medium h-6 truncate">{label}</div>
        </Tooltip>
      )}

      <div className="flex-1 flex items-center gap-2 flex-wrap">
        {slots.map((slot, idx) => {
          // Calculate minimum allowed "from" time based on previous slot
          const prevSlot = idx > 0 ? slots[idx - 1] : null;
          const minFromTime = prevSlot
            ? addMinutesToTime(prevSlot.to, 15)
            : "00:00";
          const maxFromTime = "23:30";

          // Calculate minimum allowed "to" time based on current "from"
          const minToTime = addMinutesToTime(slot.from, 15);

          return (
            <div key={idx} className="flex items-center gap-1">
              <Badge
                variant="outline"
                className="bg-muted/50 px-0 pl-2 h-6 flex items-center gap-1"
              >
                <Select
                  value={slot.from}
                  onValueChange={(v) => onUpdateSlot(idx, "from", v)}
                  open={openFromIndex === idx}
                  onOpenChange={(open) => setOpenFromIndex(open ? idx : null)}
                >
                  <SelectTrigger className="h-7 w-16 text-xs border-0 bg-transparent p-0 focus:ring-0">
                    <SelectValue>{slot.from}</SelectValue>
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px] overflow-y-auto">
                    {openFromIndex === idx &&
                      TIME_OPTIONS.filter(
                        (time) => time >= minFromTime && time <= maxFromTime,
                      ).map((time) => (
                        <SelectItem key={time} value={time} className="text-xs">
                          {time}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>

                <span className="text-muted-foreground font-normal px-1">
                  <ArrowRightIcon className="h-3 w-3" weight="fill" />
                </span>

                <Select
                  value={slot.to}
                  onValueChange={(v) => onUpdateSlot(idx, "to", v)}
                  open={openToIndex === idx}
                  onOpenChange={(open) => setOpenToIndex(open ? idx : null)}
                >
                  <SelectTrigger className="h-7 w-16 text-xs border-0 bg-transparent p-0 focus:ring-0">
                    <SelectValue>{slot.to}</SelectValue>
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px] overflow-y-auto">
                    {openToIndex === idx &&
                      TIME_OPTIONS.filter((time) => time >= minToTime).map(
                        (time) => (
                          <SelectItem
                            key={time}
                            value={time}
                            className="text-xs"
                          >
                            {time}
                          </SelectItem>
                        ),
                      )}
                  </SelectContent>
                </Select>

                <Tooltip content={t("timeSlots.removeSlot")}>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 hover:text-red-600 hover:opacity-100 p-0 opacity-25"
                    onClick={() => onRemoveSlot(idx)}
                  >
                    <TrashIcon weight="fill" />
                  </Button>
                </Tooltip>
              </Badge>
            </div>
          );
        })}

        {showAddButton && (
          <Tooltip content={t("timeSlots.addSlot")}>
            <Button
              type="button"
              variant="outline"
              className="group-hover/slots:inline-flex hidden"
              size="xsicon"
              onClick={onAddSlot}
            >
              <Plus />
            </Button>
          </Tooltip>
        )}

        {!slots.length && (
          <div className="text-muted-foreground text-sm group-hover/slots:hidden inline-block">
            {t("days.closed")}
          </div>
        )}
      </div>
    </div>
  );
}
